import DeckGL from "@deck.gl/react";
import { useEffect, useMemo, useState } from "react";
import { TileLayer } from "@deck.gl/geo-layers";
import { BitmapBoundingBox, BitmapLayer, IconLayer } from "@deck.gl/layers";
import { coordinates } from "../exports/mapcoordinates";
import { IMemberListData } from "../exports/interfaces";
import { fetchMemberLists } from "../api/MemberApi";
import { dynamicFailed } from "../hooks/useToast";
// import { WebMercatorViewport } from "@deck.gl/core";
import useMobile from "../hooks/useMobile";
// import useTab from "../hooks/useTab";

// const INITIAL_VIEW_STATE = {
//   longitude: 0,
//   latitude: 0,
//   zoom: -0.6,
//   pitch: 0,
//   bearing: 0,
// };

const getFlagURL = (code: string) => {
  return `https://flagcdn.com/w320/${code.toLowerCase()}.png`;
};

const Map = () => {
  const [listMemberData, setListMemberData] = useState<IMemberListData[]>([]);
  // const [viewState, setViewState] = useState(INITIAL_VIEW_STATE);

  const mobile = useMobile();
  // const tab = useTab();

  const INITIAL_VIEW_STATE = useMemo(() => ({
    longitude: 0,
    latitude: 0,
    zoom: mobile ? -0.6 : 0.3,
    pitch: 0,
    bearing: 0,
  }), [mobile]);

  useEffect(() => {
    fetchMemberLists()
      .then((data) => {
        const activeMembers = data.filter(
          (member: IMemberListData) => member.attributes?.Status === "Active"
        );
        setListMemberData(activeMembers);
      })
      .catch(() => dynamicFailed("Server Error"));
  }, []);

  const uniqueCountries = useMemo(() => {
    const seen = new Set<string>();
    return listMemberData
      .map((item) => item.attributes.Country?.trim())
      .filter((country): country is string => {
        if (!country || seen.has(country)) return false;
        seen.add(country);
        return true;
      });
  }, [listMemberData]);

  const countryPoints = useMemo(() => {
    return uniqueCountries
      .map((country) => {
        const match = coordinates.find(
          (coord) => coord.name.toLowerCase() === country.toLowerCase()
        );
        if (match && match.country_code) {
          return {
            name: match.name,
            position: [match.latlng[1], match.latlng[0]],
            country_code: match.country_code,
          };
        }
        return null;
      })
      .filter(Boolean) as {
      name: string;
      position: number[];
      country_code: string;
    }[];
  }, [uniqueCountries]);

  // useEffect(() => {
  //   if (countryPoints.length === 0) return;

  //   const lons = countryPoints.map((p) => p.position[0]);
  //   const lats = countryPoints.map((p) => p.position[1]);

  //   const minLng = Math.min(...lons);
  //   const maxLng = Math.max(...lons);
  //   const minLat = Math.min(...lats);
  //   const maxLat = Math.max(...lats);

  //   const viewport = new WebMercatorViewport({
  //     width: window.innerWidth,
  //     height: window.innerHeight,
  //   });

  //   const padding = mobile
  //     ? Math.min(window.innerWidth, window.innerHeight) * 0.1
  //     : 200;

  //   const zoomOut = mobile || tab ? 1.3 : 1.5;

  //   const { longitude, latitude, zoom } = viewport.fitBounds(
  //     [
  //       [minLng, minLat],
  //       [maxLng, maxLat],
  //     ],
  //     { padding }
  //   );

  //   setViewState({
  //     longitude,
  //     latitude,
  //     zoom: countryPoints.length === 1 ? 3 : zoom - zoomOut,
  //     pitch: 0,
  //     bearing: 0,
  //   });
  // }, [countryPoints, mobile, tab]);

  const tileLayerBase = (idSuffix: string, offset: number) =>
    new TileLayer({
      id: `tile-layer-${idSuffix}`,
      data: "https://basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}.png",
      minZoom: 0,
      maxZoom: 19,
      tileSize: 256,
      renderSubLayers: (props) => {
        const { boundingBox } = props.tile;
        const shiftedBounds = boundingBox.map(([lng, lat]) => [
          lng + offset,
          lat,
        ]) as BitmapBoundingBox;

        return new BitmapLayer(props, {
          data: undefined,
          image: props.data,
          bounds: shiftedBounds.flatMap((x) => x) as BitmapBoundingBox,
        });
      },
    });

  const layers = useMemo(() => {
    const tileLayers = [
      tileLayerBase("left-1800", -1800),
      tileLayerBase("left-1440", -1440),
      tileLayerBase("left-1080", -1080),
      tileLayerBase("left-720", -720),
      tileLayerBase("left-360", -360),
      tileLayerBase("base", 0),
      tileLayerBase("right-360", 360),
      tileLayerBase("right-720", 720),
      tileLayerBase("right-1080", 1080),
      tileLayerBase("right-1440", 1440),
      tileLayerBase("right-1800", 1800),
    ];

    const flagIconLayer = new IconLayer({
      id: "flag-icons",
      data: countryPoints,
      pickable: true,
      sizeScale: 10,
      getPosition: (d) => d.position,
      getIcon: (d) => ({
        url: getFlagURL(d.country_code),
        width: 80,
        height: 60,
        anchorY: 30,
      }),
      getSize: 2,
    });

    return [...tileLayers, flagIconLayer];
  }, [countryPoints]);

  return (
    // <div style={{ width: "100%", height: "55vh", position: "relative", overflow: "hidden" }}>
    <DeckGL
      viewState={INITIAL_VIEW_STATE}
      //       onViewStateChange={({ viewState }) =>
      //   setViewState(viewState as typeof INITIAL_VIEW_STATE)
      // }
      controller={{
        dragPan: false,
        dragRotate: false,
        scrollZoom: false,
        doubleClickZoom: false,
        touchZoom: false,
        touchRotate: false,
        keyboard: false,
      }}
      layers={layers}
      getTooltip={({ object }) =>
        object && {
          html: `<div style="font-size: 8px; font-weight: bold;">${object.name}</div>`,
          style: {
            backgroundColor: "#222",
            color: "#fff",
            padding: "2px 6px",
            marginTop: "20px",
          },
        }
      }
      getCursor={() => "default"}
    />
    // </div>
  );
};

export default Map;
